/* eslint-disable @doxyme/no-onclick-on-box */
import {
  type ReactNode,
  useRef,
  type CSSProperties,
  type JSX,
  type KeyboardEvent,
} from 'react';
import Box from '@mui/material/Box';
import { useTheme } from '../../../theme';
import { useSegmentedControlStyles } from './segmentedControl.styles';

type Option<T> = { readonly id: T; readonly label: ReactNode };

export type SegmentedControlProps<T> = {
  options: readonly Option<T>[];
  value: T;
  width?: CSSProperties['width'];
  onSelect: (tab: T) => void;
};

export const SegmentedControl = <T extends string>({
  options,
  value,
  onSelect,
  width,
}: SegmentedControlProps<T>): JSX.Element => {
  const theme = useTheme();
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

  const styles = useSegmentedControlStyles({
    active: options.findIndex(({ id }) => id === value),
    count: options.length,
  });

  const handleKeyDown = (event: KeyboardEvent, id: T): void => {
    let nextIndex = null;

    const currentIndex = options.findIndex((option) => option.id === id);

    if (currentIndex === -1) {
      return;
    }

    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % options.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + options.length) % options.length;
    } else {
      return;
    }

    const nextOption = options[nextIndex];
    onSelect(nextOption?.id as T);

    if (tabRefs.current[nextIndex]) {
      tabRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <Box
      css={styles.container}
      sx={{
        width: width,
      }}
    >
      <Box css={styles.tabContainer} role='tablist'>
        {options.map(({ id, label }, index) => {
          const activeIndex = options.findIndex(
            (option) => option.id === value,
          );

          return (
            <Box
              id={`tab-${id}`}
              ref={(tab: HTMLDivElement | null) => {
                tabRefs.current[index] = tab;
              }}
              key={id}
              onClick={(): void => onSelect(id)}
              onKeyDown={(e) => handleKeyDown(e, id)}
              role='tab'
              aria-selected={activeIndex === index}
              tabIndex={activeIndex === index ? 0 : -1}
              css={{
                ...styles.tab,
                color:
                  activeIndex === index
                    ? theme.segmentedControl.active.text
                    : theme.segmentedControl.default.text,
                // The CSS only approach can't be used as there is a bug in Safari that prevents the use
                // of complex selectors with pseudo elements (e.g. not(:first-of-type):before)
                ...(index > 0 &&
                index !== activeIndex &&
                index !== activeIndex + 1
                  ? styles.adjacentTab
                  : null),
              }}
            >
              {label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
